// src/components/translation-modules/ListinoManager.js
import React, { useState, useEffect } from "react"
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd"
import {
  FaTrash,
  FaEdit,
  FaPlus,
  FaExclamationTriangle,
  FaTag,
  FaImage,
  FaSave,
  FaArrowLeft,
} from "react-icons/fa"
import TranslationModuleBase from "./TranslationModuleBase"
import ImageManager from "./ImageManager"

// Use the correct domain WITHOUT www.
const API_BASE_URL = "https://salumidelnorcino.com"

const ListinoManager = ({
  currentTranslations,
  otherTranslations,
  currentLang,
  secondaryLang,
  onUpdateTranslations,
  registerSubmoduleAction,
  clearSubmoduleAction,
}) => {
  const [sections, setSections] = useState({})
  const [categories, setCategories] = useState({})

  const [isEditingSection, setIsEditingSection] = useState(false)
  const [currentSection, setCurrentSection] = useState(null)

  const [isEditingItem, setIsEditingItem] = useState(false)
  const [currentItem, setCurrentItem] = useState(null)

  const [showAddSectionForm, setShowAddSectionForm] = useState(false)
  const [newSectionKey, setNewSectionKey] = useState("")
  const [newSectionTitle, setNewSectionTitle] = useState("")

  const [showAddItemForm, setShowAddItemForm] = useState(false)
  const [newItemData, setNewItemData] = useState({
    key: "",
    name: "",
    desc: "",
    categoria: "",
    img: "", // Added image field
  })

  const [confirmDeleteSection, setConfirmDeleteSection] = useState(null)
  const [confirmDeleteItem, setConfirmDeleteItem] = useState(null)

  const [collapsedSections, setCollapsedSections] = useState({})

  // Image picker state
  const [showImagePicker, setShowImagePicker] = useState(false)

  // Register for submodule actions
  useEffect(() => {
    if (isEditingSection && currentSection) {
      registerSubmoduleAction(
        handleUpdateSection,
        `Section: ${currentSection.title}`
      )
    } else if (isEditingItem && currentItem) {
      registerSubmoduleAction(handleUpdateItem, `Item: ${currentItem.name}`)
    } else if (showAddSectionForm) {
      registerSubmoduleAction(saveNewSection, "New Section")
    } else if (showAddItemForm) {
      registerSubmoduleAction(saveNewItem, "New Item")
    } else {
      clearSubmoduleAction()
    }
  }, [
    isEditingSection,
    currentSection,
    isEditingItem,
    currentItem,
    showAddSectionForm,
    showAddItemForm,
  ])

  // Carica i dati dal contesto di traduzione
  useEffect(() => {
    if (
      currentTranslations &&
      currentTranslations.listino &&
      currentTranslations.listino.sections
    ) {
      setSections(currentTranslations.listino.sections)

      // Inizializza lo stato collapsed per ogni sezione
      const initialCollapsed = {}
      Object.keys(currentTranslations.listino.sections).forEach(
        (sectionKey) => {
          initialCollapsed[sectionKey] = true
        }
      )
      setCollapsedSections(initialCollapsed)
    }

    if (
      currentTranslations &&
      currentTranslations.listino &&
      currentTranslations.listino.categories
    ) {
      setCategories(currentTranslations.listino.categories)
    }
  }, [currentTranslations])

  // Gestione del drag & drop
  const handleDragEnd = (result) => {
    const { source, destination, type } = result

    if (!destination) return

    // Se stiamo trascinando sezioni
    if (type === "section") {
      const sectionKeys = Object.keys(sections)
      const [movedSectionKey] = sectionKeys.splice(source.index, 1)
      sectionKeys.splice(destination.index, 0, movedSectionKey)

      const reorderedSections = {}
      sectionKeys.forEach((key) => {
        reorderedSections[key] = sections[key]
      })

      setSections(reorderedSections)
      saveSections(reorderedSections)
    }
    // Se stiamo trascinando item all'interno di una sezione
    else if (type === "item") {
      const sectionKey = source.droppableId
      const sectionItems = sections[sectionKey].items

      const itemKeys = Object.keys(sectionItems)
      const [movedItemKey] = itemKeys.splice(source.index, 1)
      itemKeys.splice(destination.index, 0, movedItemKey)

      const reorderedItems = {}
      itemKeys.forEach((key) => {
        reorderedItems[key] = sectionItems[key]
      })

      const updatedSections = {
        ...sections,
        [sectionKey]: {
          ...sections[sectionKey],
          items: reorderedItems,
        },
      }

      setSections(updatedSections)
      saveSections(updatedSections)
    }
  }

  // Salva le sezioni aggiornate nel contesto di traduzione
  const saveSections = (updatedSections) => {
    const updatedCurrentTranslations = {
      ...currentTranslations,
      listino: {
        ...currentTranslations.listino,
        sections: updatedSections,
      },
    }

    // Assicurati che anche la lingua secondaria abbia la stessa struttura
    let updatedOtherTranslations = { ...otherTranslations }

    if (!updatedOtherTranslations.listino) {
      updatedOtherTranslations.listino = {}
    }

    if (!updatedOtherTranslations.listino.sections) {
      updatedOtherTranslations.listino.sections = {}
    }

    // Assicurati che ogni sezione esista nella lingua secondaria
    Object.keys(updatedSections).forEach((sectionKey) => {
      if (!updatedOtherTranslations.listino.sections[sectionKey]) {
        updatedOtherTranslations.listino.sections[sectionKey] = {
          title: updatedSections[sectionKey].title,
          items: {},
        }
      }

      // Assicurati che gli item abbiano la stessa struttura e ordine
      const currentItems = updatedSections[sectionKey].items || {}
      const otherItems =
        updatedOtherTranslations.listino.sections[sectionKey].items || {}

      const syncedItems = {}
      Object.keys(currentItems).forEach((itemKey) => {
        syncedItems[itemKey] = otherItems[itemKey] || {
          ...currentItems[itemKey],
        }

        // Assicurati che i campi img e categoria siano sincronizzati
        if (currentItems[itemKey].img) {
          syncedItems[itemKey].img = currentItems[itemKey].img
        }
        if (currentItems[itemKey].categoria) {
          syncedItems[itemKey].categoria = currentItems[itemKey].categoria
        }
      })

      updatedOtherTranslations.listino.sections[sectionKey].items = syncedItems
    })

    onUpdateTranslations(updatedCurrentTranslations, updatedOtherTranslations)
  }

  // Toggle stato collassato di una sezione
  const toggleSectionCollapse = (sectionKey) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }))
  }

  // Gestione modifica sezione
  const handleEditSection = (sectionKey) => {
    setCurrentSection({
      key: sectionKey,
      title: sections[sectionKey].title,
    })
    setIsEditingSection(true)
  }

  const handleUpdateSection = () => {
    if (!currentSection) return

    const updatedSections = {
      ...sections,
      [currentSection.key]: {
        ...sections[currentSection.key],
        title: currentSection.title,
      },
    }

    setSections(updatedSections)
    saveSections(updatedSections)
    setIsEditingSection(false)
    setCurrentSection(null)
  }

  // Gestione eliminazione sezione
  const handleDeleteSection = (sectionKey) => {
    setConfirmDeleteSection(sectionKey)
  }

  const confirmDeleteSectionAction = () => {
    if (!confirmDeleteSection) return

    const { [confirmDeleteSection]: _, ...remainingSections } = sections
    setSections(remainingSections)
    saveSections(remainingSections)
    setConfirmDeleteSection(null)
  }

  // Gestione aggiunta sezione
  const handleAddSection = () => {
    setNewSectionKey("")
    setNewSectionTitle("")
    setShowAddSectionForm(true)
  }

  const saveNewSection = () => {
    if (!newSectionKey || !newSectionTitle) {
      alert("Section key and title are required.")
      return
    }

    // Controlla se la chiave esiste già
    if (sections[newSectionKey]) {
      alert("This section key already exists. Please choose a different key.")
      return
    }

    const updatedSections = {
      ...sections,
      [newSectionKey]: {
        title: newSectionTitle,
        items: {},
      },
    }

    setSections(updatedSections)
    saveSections(updatedSections)
    setShowAddSectionForm(false)
  }

  // Gestione modifica item
  const handleEditItem = (sectionKey, itemKey) => {
    setCurrentItem({
      sectionKey,
      key: itemKey,
      ...sections[sectionKey].items[itemKey],
    })
    setIsEditingItem(true)
  }

  const handleUpdateItem = () => {
    if (!currentItem) return

    const { sectionKey, key, ...itemData } = currentItem

    const updatedSections = {
      ...sections,
      [sectionKey]: {
        ...sections[sectionKey],
        items: {
          ...sections[sectionKey].items,
          [key]: itemData,
        },
      },
    }

    setSections(updatedSections)
    saveSections(updatedSections)
    setIsEditingItem(false)
    setCurrentItem(null)
  }

  // Gestione eliminazione item
  const handleDeleteItem = (sectionKey, itemKey) => {
    setConfirmDeleteItem({ sectionKey, itemKey })
  }

  const confirmDeleteItemAction = () => {
    if (!confirmDeleteItem) return

    const { sectionKey, itemKey } = confirmDeleteItem

    const { [itemKey]: _, ...remainingItems } = sections[sectionKey].items

    const updatedSections = {
      ...sections,
      [sectionKey]: {
        ...sections[sectionKey],
        items: remainingItems,
      },
    }

    setSections(updatedSections)
    saveSections(updatedSections)
    setConfirmDeleteItem(null)
  }

  // Gestione aggiunta item
  const handleAddItem = (sectionKey) => {
    setNewItemData({
      key: "",
      name: "",
      desc: "",
      categoria: Object.keys(categories).find((k) => k !== "all") || "",
      img: "", // Initialize image field
    })
    setShowAddItemForm(sectionKey)
  }

  const saveNewItem = () => {
    if (!newItemData.key || !newItemData.name) {
      alert("Item key and name are required.")
      return
    }

    // Controlla se la chiave esiste già
    if (sections[showAddItemForm].items[newItemData.key]) {
      alert("This item key already exists. Please choose a different key.")
      return
    }

    const updatedSections = {
      ...sections,
      [showAddItemForm]: {
        ...sections[showAddItemForm],
        items: {
          ...sections[showAddItemForm].items,
          [newItemData.key]: {
            name: newItemData.name,
            desc: newItemData.desc,
            categoria: newItemData.categoria,
            img: newItemData.img, // Include image
          },
        },
      },
    }

    setSections(updatedSections)
    saveSections(updatedSections)
    setShowAddItemForm(false)
  }

  // Gestione aggiunta categoria
  const handleAddCategory = () => {
    const categoryKey = prompt("Enter new category key (lowercase, no spaces):")
    if (!categoryKey) return

    const formattedKey = categoryKey.toLowerCase().replace(/[^a-z0-9_]/g, "")

    if (categories[formattedKey]) {
      alert("This category already exists.")
      return
    }

    const categoryName = prompt("Enter category display name:")
    if (!categoryName) return

    const updatedCategories = {
      ...categories,
      [formattedKey]: categoryName,
    }

    const updatedCurrentTranslations = {
      ...currentTranslations,
      listino: {
        ...currentTranslations.listino,
        categories: updatedCategories,
      },
    }

    // Aggiorna anche nell'altra lingua
    const updatedOtherTranslations = {
      ...otherTranslations,
      listino: {
        ...otherTranslations.listino,
        categories: {
          ...otherTranslations.listino?.categories,
          [formattedKey]: categoryName,
        },
      },
    }

    setCategories(updatedCategories)
    onUpdateTranslations(updatedCurrentTranslations, updatedOtherTranslations)
  }

  // Handle image selection
  const handleImageSelected = (imagePath) => {
    if (isEditingItem && currentItem) {
      setCurrentItem({
        ...currentItem,
        img: imagePath,
      })
    } else if (showAddItemForm) {
      setNewItemData({
        ...newItemData,
        img: imagePath,
      })
    }
    setShowImagePicker(false)
  }

  // Helper function to render image with correct domain
  const renderProductImage = (imagePath) => {
    if (!imagePath) return null

    const fullImagePath = imagePath.startsWith("/")
      ? `${API_BASE_URL}${imagePath}`
      : imagePath

    return (
      <img
        src={`${fullImagePath}?_=${new Date().getTime()}`}
        alt="Product preview"
        className="w-20 h-20 object-cover border rounded"
        onError={(e) => {
          e.target.onerror = null
          e.target.src =
            'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect><circle cx="8.5" cy="8.5" r="1.5"></circle><polyline points="21 15 16 10 5 21"></polyline></svg>'
        }}
      />
    )
  }

  return (
    <TranslationModuleBase title="Listino Manager">
      {/* Visualizzazione principale */}
      {!isEditingSection &&
        !isEditingItem &&
        !showAddSectionForm &&
        !showAddItemForm &&
        !showImagePicker && (
          <>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-blue-800">
                Sezioni del Listino
              </h3>
              <div className="flex gap-2">
                <button
                  onClick={handleAddCategory}
                  className="px-3 py-1.5 bg-purple-600 text-white rounded hover:bg-purple-700 flex items-center gap-1 shadow-sm transition-colors"
                >
                  <FaTag className="text-xs" /> Aggiungi Categoria
                </button>
                <button
                  onClick={handleAddSection}
                  className="px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-1 shadow-sm transition-colors"
                >
                  <FaPlus className="text-xs" /> Aggiungi Sezione
                </button>
              </div>
            </div>

            {/* Visualizzazione delle categorie */}
            <div className="mb-8 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h4 className="font-medium mb-3 text-blue-800">
                Categorie disponibili
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {Object.entries(categories).map(([key, value]) => (
                  <div
                    key={key}
                    className="bg-white p-2 rounded border border-blue-100 flex justify-between items-center shadow-sm"
                  >
                    <div>
                      <span className="text-xs text-blue-500">{key}</span>
                      <div className="font-medium">{value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="sections" type="section">
                {(provided) => (
                  <div
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    className="space-y-4"
                  >
                    {Object.keys(sections).length === 0 ? (
                      <div className="text-center p-6 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-gray-600 mb-4">
                          Non ci sono ancora sezioni nel listino. Crea una nuova
                          sezione per iniziare.
                        </p>
                        <button
                          onClick={handleAddSection}
                          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center gap-2 mx-auto shadow-sm transition-colors"
                        >
                          <FaPlus /> Aggiungi Sezione
                        </button>
                      </div>
                    ) : (
                      Object.keys(sections).map((sectionKey, index) => (
                        <Draggable
                          key={sectionKey}
                          draggableId={sectionKey}
                          index={index}
                        >
                          {(provided) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              className="border rounded-lg overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow"
                            >
                              <div
                                {...provided.dragHandleProps}
                                className="bg-blue-50 p-3 flex justify-between items-center border-b"
                              >
                                <div className="flex items-center">
                                  <button
                                    onClick={() =>
                                      toggleSectionCollapse(sectionKey)
                                    }
                                    className="mr-2 text-blue-600 hover:text-blue-800 w-6 h-6 flex items-center justify-center rounded-full hover:bg-blue-100 transition-colors"
                                  >
                                    {collapsedSections[sectionKey] ? "►" : "▼"}
                                  </button>
                                  <h3 className="font-medium text-blue-800">
                                    {sections[sectionKey].title}
                                    <span className="text-gray-500 text-sm ml-2">
                                      ({sectionKey})
                                    </span>
                                  </h3>
                                </div>
                                <div className="flex space-x-2">
                                  <button
                                    onClick={() => handleAddItem(sectionKey)}
                                    className="p-1.5 text-green-600 hover:text-green-800 hover:bg-green-50 rounded transition-colors"
                                    title="Aggiungi prodotto"
                                  >
                                    <FaPlus />
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleEditSection(sectionKey)
                                    }
                                    className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                                    title="Modifica sezione"
                                  >
                                    <FaEdit />
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleDeleteSection(sectionKey)
                                    }
                                    className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                                    title="Elimina sezione"
                                  >
                                    <FaTrash />
                                  </button>
                                </div>
                              </div>

                              {!collapsedSections[sectionKey] && (
                                <div className="p-4">
                                  <Droppable
                                    droppableId={sectionKey}
                                    type="item"
                                  >
                                    {(provided) => (
                                      <div
                                        {...provided.droppableProps}
                                        ref={provided.innerRef}
                                        className="space-y-3"
                                      >
                                        {!sections[sectionKey].items ||
                                        Object.keys(sections[sectionKey].items)
                                          .length === 0 ? (
                                          <div className="text-center p-4 bg-gray-50 rounded-lg border border-gray-200">
                                            <p className="text-gray-600 mb-2">
                                              Nessun prodotto in questa sezione.
                                            </p>
                                            <button
                                              onClick={() =>
                                                handleAddItem(sectionKey)
                                              }
                                              className="px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 inline-flex items-center gap-1 shadow-sm transition-colors"
                                            >
                                              <FaPlus className="text-xs" />{" "}
                                              Aggiungi Prodotto
                                            </button>
                                          </div>
                                        ) : (
                                          Object.keys(
                                            sections[sectionKey].items
                                          ).map((itemKey, itemIndex) => {
                                            const item =
                                              sections[sectionKey].items[
                                                itemKey
                                              ]
                                            const categoryName =
                                              categories[item.categoria] ||
                                              "Categoria non trovata"

                                            return (
                                              <Draggable
                                                key={itemKey}
                                                draggableId={`${sectionKey}-${itemKey}`}
                                                index={itemIndex}
                                              >
                                                {(provided) => (
                                                  <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    className="p-3 border rounded-lg flex justify-between items-center bg-white hover:bg-gray-50 shadow-sm transition-colors"
                                                  >
                                                    <div className="flex gap-3 items-center">
                                                      {/* Product Image */}
                                                      <div className="flex-shrink-0 w-16 h-16 bg-gray-50/30 backdrop-blur-lg rounded border overflow-hidden flex items-center justify-center">
                                                        {item.img ? (
                                                          renderProductImage(
                                                            item.img
                                                          )
                                                        ) : (
                                                          <FaImage className="text-gray-400 text-xl" />
                                                        )}
                                                      </div>

                                                      {/* Product Info */}
                                                      <div>
                                                        <div className="font-medium text-blue-800">
                                                          {item.name}
                                                        </div>
                                                        <div className="text-sm text-gray-600 max-w-lg truncate">
                                                          {item.desc}
                                                        </div>
                                                        <div className="mt-1 text-xs inline-block bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                                                          {categoryName}
                                                        </div>
                                                      </div>
                                                    </div>
                                                    <div className="flex space-x-2">
                                                      <button
                                                        onClick={() =>
                                                          handleEditItem(
                                                            sectionKey,
                                                            itemKey
                                                          )
                                                        }
                                                        className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
                                                        title="Modifica prodotto"
                                                      >
                                                        <FaEdit />
                                                      </button>
                                                      <button
                                                        onClick={() =>
                                                          handleDeleteItem(
                                                            sectionKey,
                                                            itemKey
                                                          )
                                                        }
                                                        className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                                                        title="Elimina prodotto"
                                                      >
                                                        <FaTrash />
                                                      </button>
                                                    </div>
                                                  </div>
                                                )}
                                              </Draggable>
                                            )
                                          })
                                        )}
                                        {provided.placeholder}
                                      </div>
                                    )}
                                  </Droppable>
                                </div>
                              )}
                            </div>
                          )}
                        </Draggable>
                      ))
                    )}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>

            {/* Avviso sul form */}
            <div className="mt-8 bg-amber-50 border border-amber-200 p-4 rounded-lg">
              <div className="flex items-start">
                <FaExclamationTriangle className="text-amber-500 mt-1 mr-3 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-amber-800 mb-2">
                    Informazione sul form di contatto
                  </h4>
                  <p className="text-sm text-amber-700">
                    Il form di contatto nella pagina Listino permette ai
                    visitatori di selezionare i prodotti di loro interesse. I
                    prodotti che aggiungi qui saranno automaticamente
                    disponibili per la selezione.
                  </p>
                </div>
              </div>
            </div>
          </>
        )}

      {/* Form Modifica Sezione */}
      {isEditingSection && currentSection && (
        <div className="space-y-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-blue-800 flex items-center">
              <FaEdit className="mr-2" /> Modifica Sezione
            </h3>
            <button
              onClick={() => {
                setIsEditingSection(false)
                setCurrentSection(null)
              }}
              className="text-sm flex items-center text-gray-500 hover:text-gray-700 py-1 px-2 rounded hover:bg-gray-50/30 backdrop-blur-lg transition-colors"
            >
              <FaArrowLeft className="mr-1" /> Annulla
            </button>
          </div>

          <div className="space-y-3 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div>
              <label className="block text-sm font-medium mb-1 text-blue-700">
                Chiave Sezione
              </label>
              <input
                type="text"
                value={currentSection.key}
                disabled
                className="w-full p-2 border rounded bg-gray-50/30 backdrop-blur-lg text-gray-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Le chiavi delle sezioni non possono essere modificate dopo la
                creazione.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 text-blue-700">
                Titolo ({currentLang.toUpperCase()})
              </label>
              <input
                type="text"
                value={currentSection.title}
                onChange={(e) =>
                  setCurrentSection({
                    ...currentSection,
                    title: e.target.value,
                  })
                }
                className="w-full p-2 border border-blue-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 text-blue-700">
                Titolo ({secondaryLang.toUpperCase()})
              </label>
              <input
                type="text"
                value={
                  otherTranslations?.listino?.sections?.[currentSection.key]
                    ?.title || ""
                }
                onChange={(e) => {
                  const updatedOther = { ...otherTranslations }

                  if (!updatedOther.listino) updatedOther.listino = {}
                  if (!updatedOther.listino.sections)
                    updatedOther.listino.sections = {}
                  if (!updatedOther.listino.sections[currentSection.key]) {
                    updatedOther.listino.sections[currentSection.key] = {
                      title: e.target.value,
                      items: {},
                    }
                  } else {
                    updatedOther.listino.sections[currentSection.key] = {
                      ...updatedOther.listino.sections[currentSection.key],
                      title: e.target.value,
                    }
                  }

                  onUpdateTranslations(currentTranslations, updatedOther)
                }}
                className="w-full p-2 border border-blue-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6 sticky bottom-0">
            <button
              onClick={() => {
                setIsEditingSection(false)
                setCurrentSection(null)
              }}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 flex items-center transition-colors"
            >
              <FaArrowLeft className="mr-2" /> Annulla
            </button>
            <button
              onClick={handleUpdateSection}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center shadow-md transition-colors"
            >
              <FaSave className="mr-2" /> Salva Modifiche
            </button>
          </div>
        </div>
      )}

      {/* Form Aggiunta Sezione */}
      {showAddSectionForm && (
        <div className="space-y-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-green-800 flex items-center">
              <FaPlus className="mr-2" /> Aggiungi Nuova Sezione
            </h3>
            <button
              onClick={() => setShowAddSectionForm(false)}
              className="text-sm flex items-center text-gray-500 hover:text-gray-700 py-1 px-2 rounded hover:bg-gray-50/30 backdrop-blur-lg transition-colors"
            >
              <FaArrowLeft className="mr-1" /> Annulla
            </button>
          </div>

          <div className="space-y-3 p-4 bg-green-50 rounded-lg border border-green-200">
            <div>
              <label className="block text-sm font-medium mb-1 text-green-700">
                Chiave Sezione
              </label>
              <input
                type="text"
                value={newSectionKey}
                onChange={(e) =>
                  setNewSectionKey(
                    e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "")
                  )
                }
                className="w-full p-2 border border-green-300 rounded focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="es. salumi_tipici (minuscolo, senza spazi)"
              />
              <p className="text-xs text-gray-600 mt-1">
                Usa solo lettere minuscole, numeri e underscore. Nessuno spazio
                o caratteri speciali.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 text-green-700">
                Titolo
              </label>
              <input
                type="text"
                value={newSectionTitle}
                onChange={(e) => setNewSectionTitle(e.target.value)}
                className="w-full p-2 border border-green-300 rounded focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="es. Salumi Tipici"
              />
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6 sticky bottom-0">
            <button
              onClick={() => setShowAddSectionForm(false)}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 flex items-center transition-colors"
            >
              <FaArrowLeft className="mr-2" /> Annulla
            </button>
            <button
              onClick={saveNewSection}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center shadow-md transition-colors"
            >
              <FaSave className="mr-2" /> Aggiungi Sezione
            </button>
          </div>
        </div>
      )}

      {/* Form Modifica Item */}
      {isEditingItem && currentItem && !showImagePicker && (
        <div className="space-y-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-blue-800 flex items-center">
              <FaEdit className="mr-2" /> Modifica Prodotto
            </h3>
            <button
              onClick={() => {
                setIsEditingItem(false)
                setCurrentItem(null)
              }}
              className="text-sm flex items-center text-gray-500 hover:text-gray-700 py-1 px-2 rounded hover:bg-gray-50/30 backdrop-blur-lg transition-colors"
            >
              <FaArrowLeft className="mr-1" /> Annulla
            </button>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1 text-gray-700">
                Chiave Prodotto
              </label>
              <input
                type="text"
                value={currentItem.key}
                disabled
                className="w-full p-2 border rounded bg-gray-50/30 backdrop-blur-lg text-gray-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                Le chiavi dei prodotti non possono essere modificate dopo la
                creazione.
              </p>
            </div>

            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-3">
              <h4 className="font-medium text-blue-800 border-b border-blue-200 pb-2 mb-3">
                {currentLang.toUpperCase()}
              </h4>

              <div>
                <label className="block text-sm font-medium mb-1 text-blue-700">
                  Nome
                </label>
                <input
                  type="text"
                  value={currentItem.name || ""}
                  onChange={(e) =>
                    setCurrentItem({ ...currentItem, name: e.target.value })
                  }
                  className="w-full p-2 border border-blue-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="mt-2">
                <label className="block text-sm font-medium mb-1 text-blue-700">
                  Descrizione
                </label>
                <textarea
                  value={currentItem.desc || ""}
                  onChange={(e) =>
                    setCurrentItem({ ...currentItem, desc: e.target.value })
                  }
                  className="w-full p-2 border border-blue-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows="3"
                />
              </div>

              <div className="mt-2">
                <label className="block text-sm font-medium mb-1 text-blue-700">
                  Categoria
                </label>
                <select
                  value={currentItem.categoria || ""}
                  onChange={(e) =>
                    setCurrentItem({
                      ...currentItem,
                      categoria: e.target.value,
                    })
                  }
                  className="w-full p-2 border border-blue-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Seleziona una categoria</option>
                  {Object.entries(categories)
                    .filter(([key]) => key !== "all")
                    .map(([key, value]) => (
                      <option key={key} value={key}>
                        {value}
                      </option>
                    ))}
                </select>
              </div>

              {/* Image field */}
              <div className="mt-4">
                <label className="block text-sm font-medium mb-1 text-blue-700">
                  Immagine
                </label>
                <div className="flex items-center gap-3">
                  <div className="w-24 h-24 border rounded flex items-center justify-center bg-gray-50">
                    {currentItem.img ? (
                      renderProductImage(currentItem.img)
                    ) : (
                      <FaImage className="text-gray-400 text-xl" />
                    )}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={currentItem.img || ""}
                        onChange={(e) =>
                          setCurrentItem({
                            ...currentItem,
                            img: e.target.value,
                          })
                        }
                        className="flex-1 p-2 border border-blue-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="URL immagine o percorso relativo"
                        readOnly
                      />
                      <button
                        onClick={() => setShowImagePicker(true)}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded flex items-center gap-1 transition-colors"
                      >
                        <FaImage className="text-xs" /> Scegli
                      </button>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">
                      Seleziona un'immagine dal gestore immagini
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-5 p-4 bg-red-50 rounded-lg border border-red-200">
              <h4 className="font-medium text-red-800 border-b border-red-200 pb-2 mb-3">
                {secondaryLang.toUpperCase()}
              </h4>

              <div>
                <label className="block text-sm font-medium mb-1 text-red-700">
                  Nome
                </label>
                <input
                  type="text"
                  value={
                    otherTranslations?.listino?.sections?.[
                      currentItem.sectionKey
                    ]?.items?.[currentItem.key]?.name || ""
                  }
                  onChange={(e) => {
                    const updatedOther = { ...otherTranslations }

                    if (!updatedOther.listino) updatedOther.listino = {}
                    if (!updatedOther.listino.sections)
                      updatedOther.listino.sections = {}
                    if (
                      !updatedOther.listino.sections[currentItem.sectionKey]
                    ) {
                      updatedOther.listino.sections[currentItem.sectionKey] = {
                        title: sections[currentItem.sectionKey].title,
                        items: {},
                      }
                    }
                    if (
                      !updatedOther.listino.sections[currentItem.sectionKey]
                        .items
                    ) {
                      updatedOther.listino.sections[
                        currentItem.sectionKey
                      ].items = {}
                    }
                    if (
                      !updatedOther.listino.sections[currentItem.sectionKey]
                        .items[currentItem.key]
                    ) {
                      updatedOther.listino.sections[
                        currentItem.sectionKey
                      ].items[currentItem.key] = {
                        name: e.target.value,
                        desc: "",
                        categoria: currentItem.categoria || "",
                        img: currentItem.img || "",
                      }
                    } else {
                      updatedOther.listino.sections[
                        currentItem.sectionKey
                      ].items[currentItem.key] = {
                        ...updatedOther.listino.sections[currentItem.sectionKey]
                          .items[currentItem.key],
                        name: e.target.value,
                      }
                    }

                    onUpdateTranslations(currentTranslations, updatedOther)
                  }}
                  className="w-full p-2 border border-red-300 rounded focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>

              <div className="mt-2">
                <label className="block text-sm font-medium mb-1 text-red-700">
                  Descrizione
                </label>
                <textarea
                  value={
                    otherTranslations?.listino?.sections?.[
                      currentItem.sectionKey
                    ]?.items?.[currentItem.key]?.desc || ""
                  }
                  onChange={(e) => {
                    const updatedOther = { ...otherTranslations }

                    if (!updatedOther.listino) updatedOther.listino = {}
                    if (!updatedOther.listino.sections)
                      updatedOther.listino.sections = {}
                    if (
                      !updatedOther.listino.sections[currentItem.sectionKey]
                    ) {
                      updatedOther.listino.sections[currentItem.sectionKey] = {
                        title: sections[currentItem.sectionKey].title,
                        items: {},
                      }
                    }
                    if (
                      !updatedOther.listino.sections[currentItem.sectionKey]
                        .items
                    ) {
                      updatedOther.listino.sections[
                        currentItem.sectionKey
                      ].items = {}
                    }
                    if (
                      !updatedOther.listino.sections[currentItem.sectionKey]
                        .items[currentItem.key]
                    ) {
                      updatedOther.listino.sections[
                        currentItem.sectionKey
                      ].items[currentItem.key] = {
                        name: "",
                        desc: e.target.value,
                        categoria: currentItem.categoria || "",
                        img: currentItem.img || "",
                      }
                    } else {
                      updatedOther.listino.sections[
                        currentItem.sectionKey
                      ].items[currentItem.key] = {
                        ...updatedOther.listino.sections[currentItem.sectionKey]
                          .items[currentItem.key],
                        desc: e.target.value,
                      }
                    }

                    onUpdateTranslations(currentTranslations, updatedOther)
                  }}
                  className="w-full p-2 border border-red-300 rounded focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  rows="3"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6 sticky bottom-0">
            <button
              onClick={() => {
                setIsEditingItem(false)
                setCurrentItem(null)
              }}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 flex items-center transition-colors"
            >
              <FaArrowLeft className="mr-2" /> Annulla
            </button>
            <button
              onClick={handleUpdateItem}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center shadow-md transition-colors"
            >
              <FaSave className="mr-2" /> Salva Modifiche
            </button>
          </div>
        </div>
      )}

      {/* Form Aggiunta Item */}
      {showAddItemForm && !showImagePicker && (
        <div className="space-y-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-green-800 flex items-center">
              <FaPlus className="mr-2" /> Aggiungi Prodotto a{" "}
              {sections[showAddItemForm]?.title}
            </h3>
            <button
              onClick={() => setShowAddItemForm(false)}
              className="text-sm flex items-center text-gray-500 hover:text-gray-700 py-1 px-2 rounded hover:bg-gray-50/30 backdrop-blur-lg transition-colors"
            >
              <FaArrowLeft className="mr-1" /> Annulla
            </button>
          </div>

          <div className="space-y-3 p-4 bg-green-50 rounded-lg border border-green-200">
            <div>
              <label className="block text-sm font-medium mb-1 text-green-700">
                Chiave Prodotto
              </label>
              <input
                type="text"
                value={newItemData.key}
                onChange={(e) =>
                  setNewItemData({
                    ...newItemData,
                    key: e.target.value
                      .toLowerCase()
                      .replace(/[^a-z0-9_]/g, ""),
                  })
                }
                className="w-full p-2 border border-green-300 rounded focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="es. salame_friulano (minuscolo, senza spazi)"
              />
              <p className="text-xs text-gray-600 mt-1">
                Usa solo lettere minuscole, numeri e underscore. Nessuno spazio
                o caratteri speciali.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 text-green-700">
                Nome
              </label>
              <input
                type="text"
                value={newItemData.name}
                onChange={(e) =>
                  setNewItemData({ ...newItemData, name: e.target.value })
                }
                className="w-full p-2 border border-green-300 rounded focus:ring-2 focus:ring-green-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 text-green-700">
                Descrizione
              </label>
              <textarea
                value={newItemData.desc}
                onChange={(e) =>
                  setNewItemData({ ...newItemData, desc: e.target.value })
                }
                className="w-full p-2 border border-green-300 rounded focus:ring-2 focus:ring-green-500 focus:border-transparent"
                rows="3"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1 text-green-700">
                Categoria
              </label>
              <select
                value={newItemData.categoria}
                onChange={(e) =>
                  setNewItemData({ ...newItemData, categoria: e.target.value })
                }
                className="w-full p-2 border border-green-300 rounded focus:ring-2 focus:ring-green-500 focus:border-transparent"
              >
                <option value="">Seleziona una categoria</option>
                {Object.entries(categories)
                  .filter(([key]) => key !== "all")
                  .map(([key, value]) => (
                    <option key={key} value={key}>
                      {value}
                    </option>
                  ))}
              </select>
            </div>

            {/* Image field */}
            <div className="mt-4">
              <label className="block text-sm font-medium mb-1 text-green-700">
                Immagine
              </label>
              <div className="flex items-center gap-3">
                <div className="w-24 h-24 border rounded flex items-center justify-center bg-gray-50">
                  {newItemData.img ? (
                    renderProductImage(newItemData.img)
                  ) : (
                    <FaImage className="text-gray-400 text-xl" />
                  )}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={newItemData.img || ""}
                      onChange={(e) =>
                        setNewItemData({ ...newItemData, img: e.target.value })
                      }
                      className="flex-1 p-2 border border-green-300 rounded focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="URL immagine o percorso relativo"
                      readOnly
                    />
                    <button
                      onClick={() => setShowImagePicker(true)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded flex items-center gap-1 transition-colors"
                    >
                      <FaImage className="text-xs" /> Scegli
                    </button>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">
                    Seleziona un'immagine dal gestore immagini
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6 sticky bottom-0">
            <button
              onClick={() => setShowAddItemForm(false)}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 flex items-center transition-colors"
            >
              <FaArrowLeft className="mr-2" /> Annulla
            </button>
            <button
              onClick={saveNewItem}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center shadow-md transition-colors"
            >
              <FaSave className="mr-2" /> Aggiungi Prodotto
            </button>
          </div>
        </div>
      )}

      {/* Image Picker */}
      {showImagePicker && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-blue-800 flex items-center">
              <FaImage className="mr-2" /> Seleziona un'immagine
            </h3>
            <button
              onClick={() => setShowImagePicker(false)}
              className="text-sm flex items-center text-gray-500 hover:text-gray-700 py-1 px-2 rounded hover:bg-gray-50/30 backdrop-blur-lg transition-colors"
            >
              <FaArrowLeft className="mr-1" /> Torna al form
            </button>
          </div>

          <ImageManager
            onSelectImage={handleImageSelected}
            selectedImage={isEditingItem ? currentItem?.img : newItemData?.img}
          />
        </div>
      )}

      {/* Modal Conferma Eliminazione Sezione */}
      {confirmDeleteSection && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white/50 backdrop-blur-lg p-6 rounded-lg max-w-md mx-4 w-full shadow-xl">
            <h3 className="text-xl font-bold mb-4 text-red-600">
              Conferma Eliminazione
            </h3>
            <p className="mb-4">
              Sei sicuro di voler eliminare la sezione "
              <span className="font-medium">
                {sections[confirmDeleteSection]?.title}
              </span>
              "? Questa azione eliminerà anche tutti i prodotti in questa
              sezione. Questa operazione non può essere annullata.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setConfirmDeleteSection(null)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={confirmDeleteSectionAction}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors shadow-md"
              >
                Elimina
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Conferma Eliminazione Item */}
      {confirmDeleteItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md mx-4 w-full shadow-xl">
            <h3 className="text-xl font-bold mb-4 text-red-600">
              Conferma Eliminazione
            </h3>
            <p className="mb-4">
              Sei sicuro di voler eliminare il prodotto "
              <span className="font-medium">
                {
                  sections[confirmDeleteItem.sectionKey]?.items?.[
                    confirmDeleteItem.itemKey
                  ]?.name
                }
              </span>
              "? Questa operazione non può essere annullata.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setConfirmDeleteItem(null)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={confirmDeleteItemAction}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors shadow-md"
              >
                Elimina
              </button>
            </div>
          </div>
        </div>
      )}
    </TranslationModuleBase>
  )
}

export default ListinoManager
